import React, { useEffect, useMemo, useState } from "react"
import {
  get,
  merge,
  pick,
  concat,
  map,
  toNumber,
  nth,
  tail,
  find,
  cloneDeep,
  forEach,
  isUndefined,
} from "lodash"
import dayjs from "dayjs"
import BigNumber from "bignumber.js"

import { APIResponse } from "redux/apis/types"
import store from "redux/store"
import { hideSymbol, refreshSymbol } from "redux/ducks/SymbolDetail"
import APIs from "redux/apis"
import { PAGE_NUM } from "config/const"
import uuid from "uuid"
import { pageLoadRegist } from "redux/ducks/PageLoad"

export type TabListType = Array<{
  id: number
  name: string
  component: React.FC<any>
  props: object
  domesticOnly?: boolean
}>

type SymbolDetailActionType = {
  type:
    | "SYMBOL_DETAIL_SHOW"
    | "SYMBOL_DETAIL_HIDE"
    | "SYMBOL_DETAIL_CHANGE"
    | "SYMBOL_DETAIL_CHANGE_OPTION"
  tab?: number
  option?: number
}

export type SymbolDetailStateType = {
  symbol: APIResponse.Res
  currentTab: number
  optionTab: number | null
}

export const initialSymbolDetail: SymbolDetailStateType = {
  symbol: {},
  currentTab: 0,
  optionTab: null,
}
export const symbolDetailManageReducer = (
  state: SymbolDetailStateType,
  action: SymbolDetailActionType
) => {
  switch (action.type) {
    case "SYMBOL_DETAIL_SHOW":
      return state
    case "SYMBOL_DETAIL_HIDE":
      store.dispatch(hideSymbol())
      return state
    case "SYMBOL_DETAIL_CHANGE":
      return { ...state, currentTab: action.tab }
    case "SYMBOL_DETAIL_CHANGE_OPTION":
      return state
  }
}

//settled summary
type useSettledAccountReportSummaryType = (
  symbol: APIResponse.Res,
  show: boolean
) => [APIResponse.Res]
export const useSettledAccountReportSummary: useSettledAccountReportSummaryType = (
  symbol,
  show
) => {
  const [data, setData] = useState<APIResponse.Res>()
  useEffect(() => {
    ;(async () => {
      if (show && !!symbol) {
        const d = await APIs.SymbolDetail.SettledAccountReport({
          ...pick(symbol, ["SecurityType", "Symbol"]),
          Type: 1,
        })
        setData(d)
      }
    })()
  }, [show, symbol])
  return [data]
}

//detail情報
type UseDetailInformationType = (
  symbol: APIResponse.Res,
  refresh: boolean
) => [APIResponse.Res, APIResponse.Res]
export const useDetailInformation: UseDetailInformationType = (
  symbol,
  refresh
) => {
  const [companyInfo, setCompanyInfo] = useState<APIResponse.Res>()
  const [expectData, setExpect] = useState<APIResponse.Res>()
  const loadingId = useMemo(() => uuid(), [])
  const callApi = async () => {
    store.dispatch(pageLoadRegist(loadingId, true))
    const company = await APIs.SymbolDetail.GetCompanyInfo(
      pick(symbol, ["Symbol", "SecurityType"])
    )
    const expect = await APIs.SymbolDetail.SettledAccountReport({
      ...pick(symbol, ["Symbol", "SecurityType"]),
      Type: 7,
    })
    // @ts-ignore
    expect.CurrencyCode = get(symbol, "CurrencyCode")

    if (!!get(expect, "isAxiosError")) {
      setExpect({})
    } else {
      setExpect(expect)
    }
    if (!!get(company, "isAxiosError")) {
      setCompanyInfo({})
    } else {
      setCompanyInfo(company)
    }

    store.dispatch(pageLoadRegist(loadingId, false))
  }
  useEffect(() => {
    if (!symbol) return
    callApi()
  }, [symbol])
  useEffect(() => {
    if (!refresh) return
    callApi()
    store.dispatch(refreshSymbol())
  }, [refresh])
  return [companyInfo, expectData]
}

//銘柄規制
type RequestSymbolType = {
  Symbol: string
  SecurityType: string
  Exchange: string
}
export const useGetSymbolRegulation = (
  symbol: RequestSymbolType,
  effect?: any,
  call?: boolean
) => {
  const [data, set] = useState<Array<APIResponse.Res>>()
  const loadingId = useMemo(() => uuid(), [])
  useEffect(() => {
    ;(async () => {
      if (!symbol.Symbol) return
      await store.dispatch(pageLoadRegist(loadingId, true))
      const d = await APIs.SymbolDetail.GetSymbolRegulation(symbol)
      set(get(d, "SymbolRegulation"))
      await store.dispatch(pageLoadRegist(loadingId, false))
    })()
  }, [effect, call])
  return data
}

// 時系列データ
type ChartParamsType = {
  SecurityType: number
  Symbol: string
  Exchange: number
  LegType: string
  Symbolkey: string
  From?: string | null
  Count?: number | null
  Days?: number | null
  WithoutSymbol: boolean
}
type UseChartRegsType = (
  symbol: APIResponse.Res,
  defaultCount: number,
  isLoad: boolean,
  isSp: boolean,
  loaded: Function,
  refresh: boolean
) => [APIResponse.Res, number, Function, number, Function, Date, Function]
export const useChartRegs: UseChartRegsType = (
  symbol,
  defaultCount,
  isLoad,
  isSp,
  loaded,
  refresh
) => {
  const [type, changeType] = useState<number>(isSp ? 1 : 0)
  const [d, setData] = useState<APIResponse.Res>()
  const [page, setPage] = useState<number>(0)
  const [settingDate, setDate] = useState<Date>(null)
  const loadingId = useMemo(() => uuid(), [])

  const load = async () => {
    if (!symbol) return
    setData(null)
    store.dispatch(pageLoadRegist(loadingId, true))
    const params: ChartParamsType = {
      SecurityType: get(symbol, "SecurityType"),
      Symbol: get(symbol, "Symbol"),
      Exchange: get(symbol, "Exchange"),
      LegType: !type ? "1D" : "Tick",
      WithoutSymbol: false,
      Symbolkey: "main",
    }
    if (!type) {
      if (!settingDate) {
        params.Count = defaultCount
      } else {
        params.From = dayjs(settingDate).format("YYYYMMDDHHmmss")
      }
    } else {
      params.Count = 201
      params.Days = 1
    }
    setPage(0)
    const chart = await APIs.Chart.Chart(params)
    chart.Chart.Columns = concat(chart.Chart.Columns, "ChangedPrice")
    if (!type) {
      const lastCloseIndex = chart.Chart.Columns.indexOf("ClosePrice")
      if (lastCloseIndex >= 0 && chart.Chart.LegList.length > 0) {
        chart.Chart.LegList = map(
          chart.Chart.LegList,
          (list: Array<string | number>, i: number) => {
            if (!i) {
              return concat(list, null)
            } else {
              const lastClose: number = toNumber(nth(list, lastCloseIndex))
              const prevLastClose: number = toNumber(
                nth(chart.Chart.LegList[i - 1], lastCloseIndex)
              )
              const changedPrice = Number(
                new BigNumber(lastClose).minus(prevLastClose)
              )
              return concat(list, changedPrice)
            }
          }
        )
      }
      if (!settingDate) {
        const lastLegListDate = dayjs(nth(chart.Chart.LegList[0], 1)).toDate()
        setDate(lastLegListDate)
      }
      if (!!get(chart, "isAxiosError")) {
        setData({})
      } else {
        setData(chart)
      }
      store.dispatch(pageLoadRegist(loadingId, false))
    } else {
      const openPriceeIndex = chart.Chart.Columns.indexOf("OpenPrice")
      if (openPriceeIndex >= 0 && chart.Chart.LegList.length > 0) {
        chart.Chart.LegList = tail(
          map(
            chart.Chart.LegList,
            (list: Array<string | number>, i: number) => {
              if (get(list, openPriceeIndex, false)) {
                if (!!i) {
                  const openPrice = toNumber(get(list, openPriceeIndex))
                  const lastOpenPrice = toNumber(
                    get(nth(chart.Chart.LegList, i - 1), openPriceeIndex)
                  )
                  return concat(list, openPrice - lastOpenPrice)
                } else {
                  return concat(list, null)
                }
              }
            }
          )
        )
      }
      if (!!get(chart, "isAxiosError")) {
        setData({})
      } else {
        setData(chart)
      }
      store.dispatch(pageLoadRegist(loadingId, false))
    }
  }

  useEffect(() => {
    load()
  }, [type, settingDate, symbol])
  useEffect(() => {
    if (refresh) {
      load()
      store.dispatch(refreshSymbol())
    }
  }, [refresh])
  useEffect(() => {
    ;(async () => {
      if (isLoad) {
        await load()
        await loaded()
      }
    })()
  }, [isLoad])
  return [d, type, changeType, page, setPage, settingDate, setDate]
}

//業績
type DataName =
  | "settlement"
  | "flow"
  | "quarter"
  | "dividend"
  | "history"
  | "indexes"

type SettlementData = {
  [name in DataName]: APIResponse.Res
}

type UseSettledAccountReportType = (
  symbol: { SecurityType: string; Symbol: string },
  effect?: any
) => [SettlementData, number, Function, number, Function, number, Function]

const SettledAccountReportReqType: {
  [name in DataName]: number | Array<number>
} = {
  settlement: [2, 3],
  flow: 4,
  quarter: 6,
  dividend: 10,
  history: 11,
  indexes: 20,
}

//pcとspとでリクエストが変更される場合、ここをいじれば対応される想定
const tabType: Array<{ type: number; request: Array<DataName> }> = [
  { type: 0, request: ["settlement"] },
  { type: 1, request: ["flow"] },
  { type: 2, request: ["quarter"] },
  { type: 3, request: ["dividend", "history"] },
  { type: 4, request: ["indexes"] },
]
export const useSettledAccountReport: UseSettledAccountReportType = (
  symbol,
  effect
) => {
  const [didMounted, setMount] = useState<boolean>(false)
  const [type, setType] = useState<number>(0)
  const [settlementType, setSettlementType] = useState<number>(0)
  const [quarterType, setQuarterType] = useState<number>(0)
  const [data, setData] = useState<SettlementData>({
    settlement: {},
    flow: {},
    quarter: {},
    dividend: {},
    history: {},
    indexes: {},
  })
  const loadingId = useMemo(() => uuid(), [])

  const commonParam = {
    SecurityType: symbol.SecurityType,
    Symbol: symbol.Symbol,
  }

  const callApi = async () => {
    store.dispatch(pageLoadRegist(loadingId, true))
    const requestType = find(tabType, { type: type })
    const stateData = cloneDeep(data)
    //時間的に型解決不可
    // @ts-ignore
    const request: Array<
      Promise<[DataName, APIResponse.Res]>
    > = requestType.request.map(async (param: DataName) => {
      const reqParam = SettledAccountReportReqType[param]
      if (!reqParam) return
      const reqType =
        typeof reqParam === "object" ? nth(reqParam, settlementType) : reqParam

      const data: APIResponse.Res = await APIs.SymbolDetail.SettledAccountReport(
        {
          ...commonParam,
          Type: reqType,
        }
      )
      return [param, data]
    })

    await Promise.all(request).then(data =>
      data.forEach(res => {
        if (!!get(res[1], "isAxiosError")) {
          stateData[res[0]] = {}
        } else {
          stateData[res[0]] = res[1]
        }
      })
    )
    setData(stateData)
    setMount(true)
    store.dispatch(pageLoadRegist(loadingId, false))
  }

  useEffect(() => {
    if (!symbol.Symbol && !symbol.SecurityType) return
    callApi()
  }, [type, symbol])
  useEffect(() => {
    if (effect) {
      callApi()
      store.dispatch(refreshSymbol())
    }
  }, [effect])

  useEffect(() => {
    if (!didMounted) return
    ;(async () => {
      store.dispatch(pageLoadRegist(loadingId, true))
      const settlementRes = await APIs.SymbolDetail.SettledAccountReport({
        ...commonParam,
        Type: get(SettledAccountReportReqType.settlement, settlementType),
      })
      setData({
        ...data,
        settlement: settlementRes,
      })
      store.dispatch(pageLoadRegist(loadingId, false))
    })()
  }, [settlementType])

  return [
    data,
    type,
    setType,
    settlementType,
    setSettlementType,
    quarterType,
    setQuarterType,
  ]
}

//銘柄異動
type TransferDataType = {
  capital: APIResponse.Res
  transfer: APIResponse.Res
}
type UseTransferDataType = (
  symbol: APIResponse.Res,
  effect?: any
) => TransferDataType

export const useTransferData: UseTransferDataType = (symbol, effect) => {
  const [data, setData] = useState<TransferDataType>()
  const loadingId = useMemo(() => uuid(), [])
  const callApi = async () => {
    store.dispatch(pageLoadRegist(loadingId, true))
    const params = pick(symbol, ["Symbol", "SecurityType"])
    const capitalData = await APIs.ChangedSymbol.SymbolCapitalMovements(params)
    const transferData = await APIs.ChangedSymbol.SymbolCorporateChanges(params)
    setData({
      capital: !!get(capitalData, "isAxiosError") ? {} : capitalData,
      transfer: !!get(transferData, "isAxiosError") ? {} : transferData,
    })
    store.dispatch(pageLoadRegist(loadingId, false))
  }
  useEffect(() => {
    callApi()
  }, [symbol])

  useEffect(() => {
    if (effect) {
      callApi()
      store.dispatch(refreshSymbol())
    }
  }, [effect])
  return data
}

//株主優待
type UseStockBenefitDataType = (
  symbol: APIResponse.Res,
  effect?: any
) => APIResponse.Res
export const useStockBenefitData: UseStockBenefitDataType = (
  symbol,
  effect
) => {
  const [data, setData] = useState<APIResponse.Res>()
  const loadingId = useMemo(() => uuid(), [])
  const callApi = async () => {
    store.dispatch(pageLoadRegist(loadingId, true))
    const res = await APIs.Tool.StockholderBenefit({
      _where: `Symbol='${get(symbol, "Symbol")}'`,
    })
    if (!!get(res, "isAxiosError")) {
      setData({})
    } else {
      setData(res)
    }
    store.dispatch(pageLoadRegist(loadingId, false))
  }
  useEffect(() => {
    callApi()
  }, [symbol])
  useEffect(() => {
    if (effect) {
      callApi()
      store.dispatch(refreshSymbol())
    }
  }, [effect])
  return data
}

//関連銘柄
type UseRelationSymbolRanking = (
  symbol: APIResponse.Res,
  defaultType: number,
  effect?: any,
  isSp?: boolean
) => [APIResponse.Res, number, Function, number, Function]
export const useRelationSymbolRanking: UseRelationSymbolRanking = (
  symbol,
  defaultType,
  effect,
  isSp
) => {
  const [data, setData] = useState<APIResponse.Res>()
  const [page, setPage] = useState<number>(0)
  const [type, changeType] = useState(defaultType)
  const [prevType, changePrevType] = useState(defaultType)
  const loadingId = useMemo(() => uuid(), [])

  useEffect(() => {
    changePrevType(type)
    setPage(0)
  }, [type])

  const callApi = async () => {
    store.dispatch(pageLoadRegist(loadingId, true))
    if (isSp === false || (type !== prevType && page > 0)) {
      return
    }
    const commonParam = {
      Type: 0,
      DayBefore: 0,
      SecurityType: get(symbol, "SecurityType"),
      _take: PAGE_NUM.PAGE_20,
      _skip: PAGE_NUM.PAGE_20 * page,
    }
    const JorSPsecCode = get(symbol, !type ? "JSecCode" : "SPSecCode")
    const res = await APIs.Tool.Ranking(
      JorSPsecCode
        ? merge(commonParam, {
            JorSPsecCode,
          })
        : commonParam
    )
    const companyCurrency = get(symbol, "CompanyCurrency")
    await forEach(get(res, "ResultList"), data => {
      // @ts-ignore
      data.CompanyCurrency = companyCurrency
    })
    if (!!get(res, "isAxiosError")) {
      setData({})
    } else {
      setData(res)
    }
    store.dispatch(pageLoadRegist(loadingId, false))
  }

  useEffect(() => {
    callApi()
  }, [page, type, symbol])

  useEffect(() => {
    if (type !== defaultType) changeType(defaultType)
  }, [defaultType])

  useEffect(() => {
    if (effect) {
      callApi()
    }
  }, [effect])

  return [data, page, setPage, type, changeType]
}
